import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './ValidAnalysisPage.css';
import searchIcon from '../assets/Icon/search_icon.svg';
import Multiselect from 'multiselect-react-dropdown';
import { apiService } from '../services/apiService';
import { hasPermission, PERMISSION_CODES } from '../utils/permissionUtils';
import notificationService from '../CommanService/notificationService';
import Loader from '../CommanService/Loader';

const ValidAnalysisPage = () => {
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(true);
  const [SetValidSalesInvetoeyData, SetValidAnalysisSalesInventoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [jobLookupData, setJobLookupData] = useState([]);
  const [filteredJobLookupData, setFilteredJobLookupData] = useState([]);
  
  // Filter options for Sales & Inventory Data (Left Table)
  const [leftRegions, setLeftRegions] = useState([]);
  const [leftPartTypes, setLeftPartTypes] = useState([]);
  const [leftIcNumbers, setLeftIcNumbers] = useState([]);
  const [leftSelectedRegions, setLeftSelectedRegions] = useState(['All']);
  const [leftSelectedPartTypes, setLeftSelectedPartTypes] = useState(['All']);
  const [leftSelectedIcNumbers, setLeftSelectedIcNumbers] = useState(['All']);
  
  // Filter options for BatchPricing Job Lookup Data (Right Table)
  const [rightRegions, setRightRegions] = useState([]);
  const [rightIcNumbers, setRightIcNumbers] = useState([]);
  const [rightPartTypes, setRightPartTypes] = useState([]);
  const [rightSelectedRegions, setRightSelectedRegions] = useState(['All']);
  const [rightSelectedPartTypes, setRightSelectedPartTypes] = useState(['All']);
  const [rightSelectedIcNumbers, setRightSelectedIcNumbers] = useState(['All']);
  
  // Pagination state for both tables
  const [leftTablePage, setLeftTablePage] = useState(1);
  const [rightTablePage, setRightTablePage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Search loading state for both tables
  const [leftSearchLoading, setLeftSearchLoading] = useState(false);
  const [rightSearchLoading, setRightSearchLoading] = useState(false);
  // Track whether search is applied for each table
  const [leftSearched, setLeftSearched] = useState(false);
  const [rightSearched, setRightSearched] = useState(false);
  
  // Permission check
  const canViewValidAnalysis = hasPermission(user, PERMISSION_CODES.Sufficiency_ANALYSIS);

  // // Get user from session storage
  // const getUserFromSession = () => {
  //   try {
  //     return JSON.parse(sessionStorage.getItem('user'));
  //   } catch (error) {
  //     console.error('Error parsing user from session storage:', error);
  //     return null;
  //   }
  // };

  // useEffect(() => {
  //   const userFromSession = getUserFromSession();
  //   if (userFromSession) {
  //     fetchHistoricalData();
  //   } else {
  //     // If no user in session, still try to fetch data
  //     fetchHistoricalData();
  //   }
  // }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        // First call the sales inventory API
        console.log('Starting sequential API calls...');
        const firstApiSuccess = await GetValidAnalysisSalesInvetory();
        
        // Only call the second API if the first one succeeded
        if (firstApiSuccess) {
          console.log('First API call successful, now calling second API...');
          await GetJobLookup();
          console.log('Both API calls completed successfully');
        } else {
          console.log('First API call failed, skipping second API call');
        }
      } catch (error) {
        console.error('Error in loadData sequence:', error);
      }
    };
    
    loadData();
  }, []);


  const GetJobLookup = async () => {
    try {
      const response = await apiService.Getjob_lookup();
      console.log('Job Lookup API Response:', response);
      
      if (response && response.data && response.success) {
        const data = response.data || [];
        console.log('Job Lookup Data received:', data);
        setJobLookupData(data);
        setFilteredJobLookupData(data); // Initially show all job lookup data
        
        // Extract unique values for right table filters
        const uniqueRegions = [...new Set(data.map(item => item.region))].filter(Boolean);
        const uniquePartTypes = [...new Set(data.map(item => item.part_name))].filter(Boolean);
        const uniqueIcNumbers = [...new Set(data.map(item => item.interchange_number))].filter(Boolean);
        
        // Set filter options for right table
        setRightRegions(['All', ...uniqueRegions]);
        setRightPartTypes(['All', ...uniquePartTypes]);
        setRightIcNumbers(['All', ...uniqueIcNumbers]);
      } else {
        console.log('Job Lookup API response not successful');
        setJobLookupData([]);
        setFilteredJobLookupData([]);
        setRightRegions(['All']);
        setRightPartTypes(['All']);
        setRightIcNumbers(['All']);
      }
    } catch (error) {
      console.error('Error fetching job lookup:', error);
      setJobLookupData([]);
      setFilteredJobLookupData([]);
      setRightRegions(['All']);
      setRightPartTypes(['All']);
      setRightIcNumbers(['All']);
    }
  };

  const GetValidAnalysisSalesInvetory = async () => {
    try {
      setLoading(true);
      console.log('Starting to fetch historical data...');
      
      // Call GethistoricalSales API with isValidate=false
      const response = await apiService.GetValidAnalysisSalesInventoryData();
      console.log('API Response:', response);
      
      if (response && response.data && response.success) {
        const data = response.data || [];
        console.log('API Data received:', data);
        console.log('Data length:', data.length);
        
        if (data.length > 0) {
          // Store the API response data in useState
          SetValidAnalysisSalesInventoryData(data);
          setFilteredData(data);
          
          // Extract unique values for filter dropdowns based on actual API response structure
          const uniqueRegions = [...new Set(data.map(item => item.region))].filter(Boolean);
          const uniquePartTypes = [...new Set(data.map(item => item.part_name))].filter(Boolean);
          const uniqueIcNumbers = [...new Set(data.map(item => item.interchange_number))].filter(Boolean);
          
          console.log('Raw data sample:', data.slice(0, 3));
          console.log('Unique values extracted:', { 
            uniqueRegions, 
            uniquePartTypes, 
            uniqueIcNumbers,
            regionsCount: uniqueRegions.length,
            partTypesCount: uniquePartTypes.length,
            icNumbersCount: uniqueIcNumbers.length
          });
          
          // Set filter options with "All" option for left table (Sales & Inventory)
          const leftRegionsWithAll = ['All', ...uniqueRegions];
          const leftPartTypesWithAll = ['All', ...uniquePartTypes];
          const leftIcNumbersWithAll = ['All', ...uniqueIcNumbers];
          
          console.log('Left table filter options with All:', {
            regions: leftRegionsWithAll,
            partTypes: leftPartTypesWithAll,
            icNumbers: leftIcNumbersWithAll
          });
          
          // Set the filter options in useState for left table
          setLeftRegions(leftRegionsWithAll);
          setLeftPartTypes(leftPartTypesWithAll);
          setLeftIcNumbers(leftIcNumbersWithAll);
          
          // Log all data to console by default
          console.log('All Historical Sales Data:', data);
          console.log('Data loading completed successfully');
          console.log('Current state after setting data:');
          console.log('- SetValidSalesInvetoeyData length:', data.length);
          console.log('- leftRegions options:', leftRegionsWithAll);
          console.log('- leftPartTypes options:', leftPartTypesWithAll);
        } else {
          console.log('API returned empty data');
          SetValidAnalysisSalesInventoryData([]);
          setFilteredData([]);
          setLeftPartTypes(['All']);
          setLeftRegions(['All']);
          setLeftIcNumbers(['All']);
          notificationService.showWarning('No data available from API');
        }
      } else {
        console.log('API response not successful');
        console.log('Response structure:', response);
        SetValidAnalysisSalesInventoryData([]);
        setFilteredData([]);
        setLeftPartTypes(['All']);
        setLeftRegions(['All']);
        setLeftIcNumbers(['All']);
        notificationService.showError('API response not successful');
      }
      
      console.log('First API call completed, proceeding to second API call...');
      return true; // Indicate successful completion
    } catch (error) {
      console.error('Error fetching historical data:', error);
      console.log('API call failed');
      SetValidAnalysisSalesInventoryData([]);
      setFilteredData([]);
      setLeftPartTypes(['All']);
      setLeftRegions(['All']);
      setLeftIcNumbers(['All']);
      notificationService.showError('Error loading historical data');
      return false; // Indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Cascading filter logic for left table
  const updateCascadingFilters = (partTypeSelection, regionSelection) => {
    let filteredData = [...SetValidSalesInvetoeyData];
    
    // Filter by Part Type if not "All"
    if (partTypeSelection.length > 0 && !partTypeSelection.includes('All')) {
      filteredData = filteredData.filter(item => 
        partTypeSelection.includes(item.part_name)
      );
    }
    
    // Extract regions based on filtered part types
    const availableRegions = [...new Set(filteredData.map(item => item.region))].filter(Boolean);
    const regionsWithAll = ['All', ...availableRegions];
    setLeftRegions(regionsWithAll);
    
    // If current region selection is not available in filtered data, reset to "All"
    if (regionSelection.length > 0 && !regionSelection.includes('All')) {
      const validRegions = regionSelection.filter(region => availableRegions.includes(region));
      if (validRegions.length === 0) {
        setLeftSelectedRegions(['All']);
        regionSelection = ['All'];
      } else {
        setLeftSelectedRegions(validRegions);
        regionSelection = validRegions;
      }
    }
    
    // Filter by Region if not "All"
    if (regionSelection.length > 0 && !regionSelection.includes('All')) {
      filteredData = filteredData.filter(item => 
        regionSelection.includes(item.region)
      );
    }
    
    // Extract IC Numbers based on filtered part types and regions
    const availableIcNumbers = [...new Set(filteredData.map(item => item.interchange_number))].filter(Boolean);
    const icNumbersWithAll = ['All', ...availableIcNumbers];
    setLeftIcNumbers(icNumbersWithAll);
    
    // If current IC selection is not available in filtered data, reset to "All"
    const currentIcSelection = leftSelectedIcNumbers;
    if (currentIcSelection.length > 0 && !currentIcSelection.includes('All')) {
      const validIcNumbers = currentIcSelection.filter(ic => availableIcNumbers.includes(ic));
      if (validIcNumbers.length === 0) {
        setLeftSelectedIcNumbers(['All']);
      } else {
        setLeftSelectedIcNumbers(validIcNumbers);
      }
    }
  };

  // Simple filter handlers for left table (Sales & Inventory)
  const handleLeftSelect = (selectedList, selectedItem, setter) => {
    if (selectedItem === 'All') {
      setter(['All']);
    } else {
      setter(selectedList.filter(item => item !== 'All'));
    }
    setLeftSearched(false);
    
    // Trigger cascading filter update
    if (setter === setLeftSelectedPartTypes) {
      updateCascadingFilters(selectedList.filter(item => item !== 'All'), leftSelectedRegions);
    } else if (setter === setLeftSelectedRegions) {
      updateCascadingFilters(leftSelectedPartTypes.filter(item => item !== 'All'), selectedList.filter(item => item !== 'All'));
    }
  };

  const handleLeftRemove = (selectedList, removedItem, setter) => {
    if (selectedList.length === 0) {
      setter(['All']);
    } else {
      setter(selectedList);
    }
    setLeftSearched(false);
    
    // Trigger cascading filter update
    if (setter === setLeftSelectedPartTypes) {
      updateCascadingFilters(selectedList, leftSelectedRegions);
    } else if (setter === setLeftSelectedRegions) {
      updateCascadingFilters(leftSelectedPartTypes.filter(item => item !== 'All'), selectedList);
    }
  };

  // Simple filter handlers for right table (BatchPricing Job Lookup)
  const handleRightSelect = (selectedList, selectedItem, setter) => {
    if (selectedItem === 'All') {
      setter(['All']);
    } else {
      setter(selectedList.filter(item => item !== 'All'));
    }
    setRightSearched(false);
  };

  const handleRightRemove = (selectedList, removedItem, setter) => {
    if (selectedList.length === 0) {
      setter(['All']);
    } else {
      setter(selectedList);
    }
    setRightSearched(false);
  };

  // Search function for left table (Sales & Inventory Data)
  const handleLeftSearch = () => {
    try {
      setLeftSearchLoading(true);
      
      setTimeout(() => {
        let filtered = [...SetValidSalesInvetoeyData];
        
        // Apply Part Type filter - if "All" is selected, don't filter
        if (leftSelectedPartTypes.length > 0 && !leftSelectedPartTypes.includes('All')) {
          filtered = filtered.filter(item => 
            leftSelectedPartTypes.includes(item.part_name)
          );
        }
        
        // Apply Regions filter - if "All" is selected, don't filter
        if (leftSelectedRegions.length > 0 && !leftSelectedRegions.includes('All')) {
          filtered = filtered.filter(item => 
            leftSelectedRegions.includes(item.region)
          );
        }
        
        // Apply IC Number filter - if "All" is selected, don't filter
        if (leftSelectedIcNumbers.length > 0 && !leftSelectedIcNumbers.includes('All')) {
          filtered = filtered.filter(item => 
            leftSelectedIcNumbers.includes(item.interchange_number)
          );
        }
        
        setFilteredData(filtered);
        setLeftTablePage(1); // Reset pagination
        
        console.log('Left table filtered data:', filtered);
        console.log('Left table applied filters:', {
          partTypes: leftSelectedPartTypes,
          regions: leftSelectedRegions,
          icNumbers: leftSelectedIcNumbers
        });
        
        if (leftSelectedPartTypes.includes('All') && leftSelectedRegions.includes('All') && leftSelectedIcNumbers.includes('All')) {
          notificationService.showSuccess(`Showing all ${filtered.length} sales records`);
        } else {
          notificationService.showSuccess(`Found ${filtered.length} sales records`);
        }
        
        setLeftSearched(true);
        setLeftSearchLoading(false);
      }, 500);
      
    } catch (error) {
      console.error('Error applying left table filters:', error);
      notificationService.showError('Error applying filters');
      setLeftSearchLoading(false);
    }
  };

  // Search function for right table (BatchPricing Job Lookup Data)
  const handleRightSearch = () => {
    try {
      setRightSearchLoading(true);
      
      setTimeout(() => {
        let filtered = [...jobLookupData];
        
        // Apply Part Type filter - if "All" is selected, don't filter
        if (rightSelectedPartTypes.length > 0 && !rightSelectedPartTypes.includes('All')) {
          filtered = filtered.filter(item => 
            rightSelectedPartTypes.includes(item.part_name)
          );
        }
        
        // Apply Regions filter - if "All" is selected, don't filter
        if (rightSelectedRegions.length > 0 && !rightSelectedRegions.includes('All')) {
          filtered = filtered.filter(item => 
            rightSelectedRegions.includes(item.region)
          );
        }
        
        // Apply IC Number filter - if "All" is selected, don't filter
        if (rightSelectedIcNumbers.length > 0 && !rightSelectedIcNumbers.includes('All')) {
          filtered = filtered.filter(item => 
            rightSelectedIcNumbers.includes(item.interchange_number)
          );
        }
        
        setFilteredJobLookupData(filtered);
        setRightTablePage(1); // Reset pagination
        
        console.log('Right table filtered data:', filtered);
        console.log('Right table applied filters:', {
          partTypes: rightSelectedPartTypes,
          regions: rightSelectedRegions,
          icNumbers: rightSelectedIcNumbers
        });
        
        if (rightSelectedPartTypes.includes('All') && rightSelectedRegions.includes('All') && rightSelectedIcNumbers.includes('All')) {
          notificationService.showSuccess(`Showing all ${filtered.length} job lookup records`);
        } else {
          notificationService.showSuccess(`Found ${filtered.length} job lookup records`);
        }
        
        setRightSearched(true);
        setRightSearchLoading(false);
      }, 500);
      
    } catch (error) {
      console.error('Error applying right table filters:', error);
      notificationService.showError('Error applying filters');
      setRightSearchLoading(false);
    }
  };

  // Pagination functions
  const getPaginatedData = (data, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (dataLength) => {
    return Math.ceil(dataLength / itemsPerPage);
  };

  const handlePageChange = (newPage, tableType) => {
    if (tableType === 'left') {
      setLeftTablePage(newPage);
    } else {
      setRightTablePage(newPage);
    }
  };

  // Show loader while API data is loading
  if (loading) {
    return <Loader />;
  }

  if (!canViewValidAnalysis) {
    return (
      <div className="permission-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to view Data Sufficiency for Analysis.</p>
      </div>
    );
  }

  return (
    <div className="valid-analysis-page">
      
      {/* Tables Section with Individual Filters */}
      <div className="tables-container">
        {loading && <Loader />}
        
        {/* Left Table with its own filters */}
        <div className="table-section left-table">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className="text-left p-0">Sales & Inventory Data</h3>
            {(() => {
              const leftTotalCount = SetValidSalesInvetoeyData.length;
              const showLeftFilter = leftSearched;
              const leftFilterCount = showLeftFilter ? filteredData.length : 0;
              return (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', border: '1px solid #eee', borderRadius: '6px', background: '#fafafa' }}>
                    <span className="count_blk">Total count</span>
                    <strong>{leftTotalCount}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', border: '1px solid #eee', borderRadius: '6px', background: '#fafafa' }}>
                    <span  className="count_blk">Filter Count</span>
                    <strong>{leftFilterCount}</strong>
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Left Table Filter Section */}
      <div className="filter-section"> 
        <div className="filter-container">
          <div className="filter-row">
            <div className="filter-item">
              <label>Part Type</label>
              <Multiselect
                isObject={false}
                    options={leftPartTypes}
                    selectedValues={leftSelectedPartTypes}
                onSelect={(selectedList, selectedItem) => {
                      handleLeftSelect(selectedList, selectedItem, setLeftSelectedPartTypes);
                }}
                onRemove={(selectedList, removedItem) => {
                      handleLeftRemove(selectedList, removedItem, setLeftSelectedPartTypes);
                }}
                placeholder="Select Part Type"
                style={{
                  chips: { background: '#ff6b35' },
                  searchBox: { border: '1px solid #ddd', borderRadius: '4px' }
                }}
              />
            </div>
            
            <div className="filter-item">
              <label>Regions</label>
              <Multiselect
                isObject={false}
                    options={leftRegions}
                    selectedValues={leftSelectedRegions}
                onSelect={(selectedList, selectedItem) => {
                      handleLeftSelect(selectedList, selectedItem, setLeftSelectedRegions);
                }}
                onRemove={(selectedList, removedItem) => {
                      handleLeftRemove(selectedList, removedItem, setLeftSelectedRegions);
                }}
                placeholder="Select Regions"
                style={{
                  chips: { background: '#ff6b35' },
                  searchBox: { border: '1px solid #ddd', borderRadius: '4px' }
                }}
              />
            </div>
            
            <div className="filter-item">
              <label>IC Number</label>
              <Multiselect
                isObject={false}
                    options={leftIcNumbers}
                    selectedValues={leftSelectedIcNumbers}
                onSelect={(selectedList, selectedItem) => {
                      handleLeftSelect(selectedList, selectedItem, setLeftSelectedIcNumbers);
                }}
                onRemove={(selectedList, removedItem) => {
                      handleLeftRemove(selectedList, removedItem, setLeftSelectedIcNumbers);
                }}
                placeholder="Select IC Number"
                style={{
                  chips: { background: '#ff6b35' },
                  searchBox: { border: '1px solid #ddd', borderRadius: '4px' }
                }}
              />
            </div>
            
            <div className="filter-actions">
                  <button className="search-btn" onClick={handleLeftSearch} disabled={leftSearchLoading}>
                    {leftSearchLoading ? (
                  <div className="search-loader">
                    <div></div><div></div><div></div>
                  </div>
                ) : (
                  <img src={searchIcon} alt="Search" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Part Name</th>
                  <th>Region</th>
                  <th>IC</th>
                  <th>Sold Count</th>
                  <th>Inventory Count</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  getPaginatedData(filteredData, leftTablePage, itemsPerPage).map((item, index) => (
                    <tr key={index}>
                      <td>{item.part_name || 'N/A'}</td>
                      <td>{item.region || 'N/A'}</td>
                      <td>{item.interchange_number || 'N/A'}</td>
                      <td>{item.sold_count || 0}</td>
                      <td>{item.inventory_count || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Left Table Pagination */}
            {filteredData.length > itemsPerPage && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(leftTablePage - 1, 'left')}
                  disabled={leftTablePage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {leftTablePage} of {getTotalPages(filteredData.length)}
                </span>
                <button 
                  onClick={() => handlePageChange(leftTablePage + 1, 'left')}
                  disabled={leftTablePage === getTotalPages(filteredData.length)}
                  className="pagination-btn"
                >
                  Next
                </button>
                <span className="pagination-total">
                  Total: {filteredData.length} records
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Table with its own filters */}
        <div className="table-section right-table">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className="text-left p-0"> Batch Pricing Lookup Data</h3>
            {(() => {
              const rightTotalCount = jobLookupData.length;
              const showRightFilter = rightSearched;
              const rightFilterCount = showRightFilter ? filteredJobLookupData.length : 0;
              return (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', border: '1px solid #eee', borderRadius: '6px', background: '#fafafa' }}>
                    <span className="count_blk">Total count</span>
                    <strong>{rightTotalCount}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', border: '1px solid #eee', borderRadius: '6px', background: '#fafafa' }}>
                    <span className="count_blk">Filter Count</span>
                    <strong>{rightFilterCount}</strong>
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Right Table Filter Section */}
          <div className="filter-section"> 
            <div className="filter-container">
              <div className="filter-row">
                <div className="filter-item">
                  <label>Part Type</label>
                  <Multiselect
                    isObject={false}
                    options={rightPartTypes}
                    selectedValues={rightSelectedPartTypes}
                    onSelect={(selectedList, selectedItem) => {
                      handleRightSelect(selectedList, selectedItem, setRightSelectedPartTypes);
                    }}
                    onRemove={(selectedList, removedItem) => {
                      handleRightRemove(selectedList, removedItem, setRightSelectedPartTypes);
                    }}
                    placeholder="Select Part Type"
                    style={{
                      chips: { background: '#ff6b35' },
                      searchBox: { border: '1px solid #ddd', borderRadius: '4px' }
                    }}
                  />
                </div>
                
                <div className="filter-item">
                  <label>Regions</label>
                  <Multiselect
                    isObject={false}
                    options={rightRegions}
                    selectedValues={rightSelectedRegions}
                    onSelect={(selectedList, selectedItem) => {
                      handleRightSelect(selectedList, selectedItem, setRightSelectedRegions);
                    }}
                    onRemove={(selectedList, removedItem) => {
                      handleRightRemove(selectedList, removedItem, setRightSelectedRegions);
                    }}
                    placeholder="Select Regions"
                    style={{
                      chips: { background: '#ff6b35' },
                      searchBox: { border: '1px solid #ddd', borderRadius: '4px' }
                    }}
                  />
                </div>
                
                <div className="filter-item">
                  <label>IC Number</label>
                  <Multiselect
                    isObject={false}
                    options={rightIcNumbers}
                    selectedValues={rightSelectedIcNumbers}
                    onSelect={(selectedList, selectedItem) => {
                      handleRightSelect(selectedList, selectedItem, setRightSelectedIcNumbers);
                    }}
                    onRemove={(selectedList, removedItem) => {
                      handleRightRemove(selectedList, removedItem, setRightSelectedIcNumbers);
                    }}
                    placeholder="Select IC Number"
                    style={{
                      chips: { background: '#ff6b35' },
                      searchBox: { border: '1px solid #ddd', borderRadius: '4px' }
                    }}
                  />
                </div>
                
                <div className="filter-actions">
                  <button className="search-btn" onClick={handleRightSearch} disabled={rightSearchLoading}>
                    {rightSearchLoading ? (
                      <div className="search-loader">
                        <div></div><div></div><div></div>
                      </div>
                    ) : (
                      <img src={searchIcon} alt="Search" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Part Name</th>
                  <th>Region</th>
                  <th>IC</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobLookupData.length > 0 ? (
                  getPaginatedData(filteredJobLookupData, rightTablePage, itemsPerPage).map((item, index) => (
                    <tr key={index}>
                      <td>{item.part_name || 'N/A'}</td>
                      <td>{item.region || 'N/A'}</td>
                      <td>{item.interchange_number || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Right Table Pagination */}
            {filteredJobLookupData.length > itemsPerPage && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(rightTablePage - 1, 'right')}
                  disabled={rightTablePage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {rightTablePage} of {getTotalPages(filteredJobLookupData.length)}
                </span>
                <button 
                  onClick={() => handlePageChange(rightTablePage + 1, 'right')}
                  disabled={rightTablePage === getTotalPages(filteredJobLookupData.length)}
                  className="pagination-btn"
                >
                  Next
                </button>
                <span className="pagination-total">
                  Total: {filteredJobLookupData.length} records
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidAnalysisPage;
